import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Checkbox,
  Radio,
  Upload,
  Input,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Image,
  Tag,
  Modal,
  Collapse,
  Spin,
} from 'antd';
import {
  UploadOutlined,
} from '@ant-design/icons';
import { getImageUrl } from '../../utils/imageUtils';
import './CleanProposalPDFOptions.css';

const { Panel } = Collapse;
const { Text, Title } = Typography;

const DEFAULT_PRODUCT_INFO_FIELDS = [
  { key: 'name', label: 'Product Name', default: true },
  { key: 'description', label: 'Description', default: true },
  { key: 'price', label: 'Total Price', default: true },
  { key: 'quantity', label: 'Quantity', default: true },
  { key: 'unit_price', label: 'Unit Price', default: false }
];

const DEFAULT_PIECE_INFO_FIELDS = [
  { key: 'name', label: 'Piece Name', default: true },
  { key: 'description', label: 'Description', default: true },
  { key: 'price', label: 'Price', default: true },
  { key: 'code', label: 'Manufacturer Code', default: false },
  { key: 'dimensions', label: 'Dimensions', default: false }
];

const DEFAULT_MATERIAL_INFO_FIELDS = [
  { key: 'name', label: 'Material Name', default: true },
  { key: 'color', label: 'Color', default: true },
  { key: 'finish', label: 'Finish', default: true },
  { key: 'thickness', label: 'Thickness', default: false },
  { key: 'price', label: 'Price', default: false }
];

const CleanProposalPDFOptions = ({
  proposal,
  pdfOptions,
  setPdfOptions,
  uploadingImages,
  handleImageUpload,
  editingImageId,
  editingDescription,
  setEditingImageId,
  setEditingDescription,
  handleImageDescriptionUpdate,
  handleImageDelete,
  handleDownloadPDF,
  templateType = "clean_proposal"
}) => {
  const [expandedSections, setExpandedSections] = useState({});
  const initializedRef = useRef(null);
  const processedUploadUidsRef = useRef(new Set());

  // Determine the options key based on template type
  const optionsKey = templateType === "pretty_proposal" ? "prettyProposalOptions" : "cleanProposalOptions";
  const currentOptionsForKey = pdfOptions[optionsKey];

  // Initialize options if not set
  useEffect(() => {
    const currentOptions = currentOptionsForKey;
    const needsInitialization = !currentOptions;
    const isDifferentKey = initializedRef.current !== optionsKey;
    
    if (needsInitialization) {
      initializedRef.current = optionsKey;
      setPdfOptions(prev => ({
        ...prev,
        [optionsKey]: {
          showProductImages: true,
          showProductInfo: true,
          showPieces: true,
          showPieceImages: true,
          showPieceInfo: true,
          showMaterials: true,
          showMaterialImages: true,
          showMaterialInfo: true,
          selectedProductInfoFields: DEFAULT_PRODUCT_INFO_FIELDS.filter(f => f.default).map(f => f.key),
          selectedPieceInfoFields: DEFAULT_PIECE_INFO_FIELDS.filter(f => f.default).map(f => f.key),
          selectedMaterialInfoFields: DEFAULT_MATERIAL_INFO_FIELDS.filter(f => f.default).map(f => f.key),
          selectedProductImages: {},
          selectedPieceImages: {},
          selectedMaterialImages: {},
          customDescriptions: {}
        }
      }));
    } else if (isDifferentKey) {
      initializedRef.current = optionsKey;
    }
  }, [currentOptionsForKey, optionsKey, setPdfOptions]);

  // Show loading if options not initialized yet
  if (!pdfOptions[optionsKey]) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">Loading options...</Text>
        </div>
      </div>
    );
  }

  const handleFieldToggle = (section, fieldKey) => {
    const currentFields = pdfOptions[optionsKey][`selected${section}Fields`] || [];
    const newFields = currentFields.includes(fieldKey)
      ? currentFields.filter(f => f !== fieldKey)
      : [...currentFields, fieldKey];

    setPdfOptions(prev => ({
      ...prev,
      [optionsKey]: {
        ...prev[optionsKey],
        [`selected${section}Fields`]: newFields
      }
    }));
  };

  const handleImageSelection = (type, itemId, imageId, checked) => {
    const currentSelected = pdfOptions[optionsKey][`selected${type}Images`]?.[itemId] || [];
    const newSelected = checked
      ? [...currentSelected, imageId]
      : currentSelected.filter(id => id !== imageId);

    setPdfOptions(prev => ({
      ...prev,
      [optionsKey]: {
        ...prev[optionsKey],
        [`selected${type}Images`]: {
          ...prev[optionsKey][`selected${type}Images`],
          [itemId]: newSelected
        }
      }
    }));
  };

  const handlePrimaryImageSelection = (itemId, imageId) => {
    const currentSelected = pdfOptions[optionsKey].selectedProductImages?.[itemId] || [];
    const currentPrimary = currentSelected[0] || null;
    
    // If clicking the same primary image, do nothing
    if (currentPrimary === imageId) {
      return;
    }
    
    // Remove imageId from secondary if it exists there
    const secondaryImages = currentSelected.slice(1, 4);
    const newSecondary = secondaryImages.filter(id => id !== imageId);
    
    // If there was a previous primary, add it to secondary (if there's room)
    let finalSecondary = newSecondary;
    if (currentPrimary && currentPrimary !== imageId && finalSecondary.length < 3) {
      finalSecondary = [...finalSecondary, currentPrimary];
    }
    
    // Set new primary as first, then secondary images
    const newSelected = [imageId, ...finalSecondary];

    setPdfOptions(prev => ({
      ...prev,
      [optionsKey]: {
        ...prev[optionsKey],
        selectedProductImages: {
          ...prev[optionsKey].selectedProductImages,
          [itemId]: newSelected
        }
      }
    }));
  };

  const handleSecondaryImageSelection = (itemId, imageId, checked) => {
    const currentSelected = pdfOptions[optionsKey].selectedProductImages?.[itemId] || [];
    const primaryImage = currentSelected[0] || null;
    const secondaryImages = currentSelected.slice(1, 4);

    // Don't allow selecting primary image as secondary
    if (checked && primaryImage === imageId) {
      return;
    }

    let newSecondary;
    if (checked) {
      // Add to secondary (max 3)
      if (secondaryImages.length < 3 && !secondaryImages.includes(imageId)) {
        newSecondary = [...secondaryImages, imageId];
      } else {
        newSecondary = secondaryImages; // Already at max or already selected
      }
    } else {
      // Remove from secondary
      newSecondary = secondaryImages.filter(id => id !== imageId);
    }

    // Reconstruct array: primary first, then secondary
    const newSelected = primaryImage 
      ? [primaryImage, ...newSecondary]
      : newSecondary;

    setPdfOptions(prev => ({
      ...prev,
      [optionsKey]: {
        ...prev[optionsKey],
        selectedProductImages: {
          ...prev[optionsKey].selectedProductImages,
          [itemId]: newSelected
        }
      }
    }));
  };

  const renderImageSelection = (type, itemId, images, title) => {
    if (!images || images.length === 0) return null;

    return (
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 12 }}>{title}:</Text>
        <Row gutter={[16, 16]}>
          {images.map((image) => (
            <Col xs={12} sm={8} md={6} key={image.image_id}>
              <Card
                hoverable
                style={{
                  border: pdfOptions[optionsKey][`selected${type}Images`]?.[itemId]?.includes(image.image_id)
                    ? '2px solid #1890ff'
                    : '1px solid #d9d9d9',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  const isSelected = pdfOptions[optionsKey][`selected${type}Images`]?.[itemId]?.includes(image.image_id) || false;
                  handleImageSelection(type, itemId, image.image_id, !isSelected);
                }}
              >
                <div style={{ textAlign: 'center', marginBottom: 8 }}>
                  <Checkbox
                    checked={pdfOptions[optionsKey][`selected${type}Images`]?.[itemId]?.includes(image.image_id) || false}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleImageSelection(type, itemId, image.image_id, e.target.checked);
                    }}
                  >
                    <Tag color={image.is_uploaded ? 'green' : image.is_primary ? 'blue' : 'default'}>
                      {image.is_uploaded ? 'Uploaded' : (image.is_primary ? 'Primary' : 'Product')}
                    </Tag>
                  </Checkbox>
                </div>
                <Image
                  src={getImageUrl(image.image_url)}
                  alt="Product"
                  style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                  preview={false}
                />
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {image.description || 'No description'}
                  </Text>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  const renderProductImageSelection = (itemId, images) => {
    if (!images || images.length === 0) return null;

    const selectedImages = pdfOptions[optionsKey].selectedProductImages?.[itemId] || [];
    const primaryImageId = selectedImages[0] || null;
    const secondaryImageIds = selectedImages.slice(1, 4); // Max 3 secondary

    const renderImageCard = (image, isPrimarySection = false, isSecondarySection = false) => {
      const isSelectedAsPrimary = primaryImageId === image.image_id;
      const isSelectedAsSecondary = secondaryImageIds.includes(image.image_id);
      const isSelected = isSelectedAsPrimary || isSelectedAsSecondary;

      return (
        <Col xs={12} sm={8} md={6} key={`${image.image_id}_${isPrimarySection ? 'primary' : 'secondary'}`}>
          <Card
            hoverable
            style={{
              border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9',
              cursor: isPrimarySection ? 'pointer' : 'default'
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: 8 }}>
              {isPrimarySection ? (
                <Radio
                  checked={isSelectedAsPrimary}
                  onChange={(e) => {
                    e.stopPropagation();
                    handlePrimaryImageSelection(itemId, image.image_id);
                  }}
                >
                  <Text strong style={{ color: '#1890ff' }}>Primary Image</Text>
                </Radio>
              ) : (
                <Checkbox
                  checked={isSelectedAsSecondary}
                  disabled={isSelectedAsPrimary || (!isSelectedAsSecondary && secondaryImageIds.length >= 3)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSecondaryImageSelection(itemId, image.image_id, e.target.checked);
                  }}
                >
                  <Text>
                    {isSelectedAsSecondary ? `Secondary ${secondaryImageIds.indexOf(image.image_id) + 1}` : 'Secondary'}
                  </Text>
                  {isSelectedAsPrimary && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>(Selected as Primary)</Text>
                    </div>
                  )}
                  {!isSelectedAsSecondary && !isSelectedAsPrimary && secondaryImageIds.length >= 3 && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>(Max 3 selected)</Text>
                    </div>
                  )}
                </Checkbox>
              )}
            </div>
            <Image
              src={getImageUrl(image.image_url)}
              alt="Product"
              style={{ width: '100%', height: '150px', objectFit: 'cover' }}
              preview={false}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {image.description || 'No description'}
              </Text>
              {isSelectedAsPrimary && (
                <div style={{ marginTop: 4 }}>
                  <Tag color="blue">Primary</Tag>
                </div>
              )}
              {isSelectedAsSecondary && (
                <div style={{ marginTop: 4 }}>
                  <Tag>Secondary {secondaryImageIds.indexOf(image.image_id) + 1}</Tag>
                </div>
              )}
            </div>
          </Card>
        </Col>
      );
    };

    return (
      <Space orientation="vertical" size="large" style={{ width: '100%', marginBottom: 16 }}>
        {/* Primary Image Section */}
        <div>
          <Text strong style={{ color: '#1890ff', display: 'block', marginBottom: 12 }}>
            Primary Image <Text type="secondary" style={{ fontWeight: 'normal' }}>(choose 1 - will appear large)</Text>
          </Text>
          <Row gutter={[16, 16]}>
            {images.map((image) => renderImageCard(image, true, false))}
          </Row>
        </div>

        {/* Secondary Images Section */}
        <div>
          <Text strong style={{ display: 'block', marginBottom: 12 }}>
            Secondary Images <Text type="secondary" style={{ fontWeight: 'normal' }}>(choose up to 3 - will appear small below primary)</Text>
          </Text>
          <Row gutter={[16, 16]}>
            {images.map((image) => renderImageCard(image, false, true))}
          </Row>
        </div>
      </Space>
    );
  };

  const renderFieldSelection = (section, fields, title) => {
    return (
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 12 }}>{title}:</Text>
        <Row gutter={[16, 8]}>
          {fields.map((field) => (
            <Col xs={24} sm={12} key={field.key}>
              <Checkbox
                checked={pdfOptions[optionsKey][`selected${section}Fields`]?.includes(field.key) || false}
                onChange={() => handleFieldToggle(section, field.key)}
              >
                {field.label}
              </Checkbox>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  const handleUploadChange = (itemId, info) => {
    const { file } = info;
    if (!file || file.status === 'removed') return;

    const dedupeKey = `${itemId}:${file.uid}`;
    if (processedUploadUidsRef.current.has(dedupeKey)) return;
    processedUploadUidsRef.current.add(dedupeKey);

    const originFile = file.originFileObj;
    if (originFile instanceof File) {
      handleImageUpload(itemId, [originFile]);
    }
  };

  // Custom upload props
  const uploadProps = (itemId) => ({
    multiple: true,
    accept: 'image/*',
    beforeUpload: () => false, // Prevent auto upload
    onChange: (info) => handleUploadChange(itemId, info),
    disabled: uploadingImages[itemId],
  });

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      <Card
        title={
          <Space orientation="vertical" size="small" style={{ width: '100%' }}>
            <Title level={5} style={{ margin: 0 }}>
              {templateType === "pretty_proposal" ? "Pretty Proposal PDF Customization" : "Clean Proposal PDF Customization"}
            </Title>
            <Text type="secondary">Customize what appears in your PDF output</Text>
          </Space>
        }
      >
          {/* Global Section Toggles */}
          <Space orientation="vertical" size="middle" style={{ width: '100%', marginBottom: 24 }}>
            <Title level={5}>Section Visibility</Title>
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={12}>
                <Space orientation="vertical" style={{ width: '100%' }}>
                  <Checkbox
                    checked={pdfOptions[optionsKey].showProductImages}
                    onChange={(e) => setPdfOptions(prev => ({
                      ...prev,
                      [optionsKey]: {
                        ...prev[optionsKey],
                        showProductImages: e.target.checked
                      }
                    }))}
                  >
                    Show Product Images
                  </Checkbox>
                  <Checkbox
                    checked={pdfOptions[optionsKey].showProductInfo}
                    onChange={(e) => setPdfOptions(prev => ({
                      ...prev,
                      [optionsKey]: {
                        ...prev[optionsKey],
                        showProductInfo: e.target.checked
                      }
                    }))}
                  >
                    Show Product Information
                  </Checkbox>
                  <Checkbox
                    checked={pdfOptions[optionsKey].showPieces}
                    onChange={(e) => setPdfOptions(prev => ({
                      ...prev,
                      [optionsKey]: {
                        ...prev[optionsKey],
                        showPieces: e.target.checked
                      }
                    }))}
                  >
                    Show Pieces Section
                  </Checkbox>
                </Space>
              </Col>
              <Col xs={24} sm={12}>
                <Space orientation="vertical" style={{ width: '100%' }}>
                  <Checkbox
                    checked={pdfOptions[optionsKey].showPieceImages}
                    onChange={(e) => setPdfOptions(prev => ({
                      ...prev,
                      [optionsKey]: {
                        ...prev[optionsKey],
                        showPieceImages: e.target.checked
                      }
                    }))}
                  >
                    Show Piece Images
                  </Checkbox>
                  <Checkbox
                    checked={pdfOptions[optionsKey].showPieceInfo}
                    onChange={(e) => setPdfOptions(prev => ({
                      ...prev,
                      [optionsKey]: {
                        ...prev[optionsKey],
                        showPieceInfo: e.target.checked
                      }
                    }))}
                  >
                    Show Piece Information
                  </Checkbox>
                  <Checkbox
                    checked={pdfOptions[optionsKey].showMaterials}
                    onChange={(e) => setPdfOptions(prev => ({
                      ...prev,
                      [optionsKey]: {
                        ...prev[optionsKey],
                        showMaterials: e.target.checked
                      }
                    }))}
                  >
                    Show Materials Section
                  </Checkbox>
                </Space>
              </Col>
            </Row>
          </Space>

          {/* Product Sections */}
          <Collapse
            activeKey={Object.keys(expandedSections).filter(key => expandedSections[key])}
            onChange={(keys) => {
              const newExpanded = {};
              if (Array.isArray(keys)) {
                keys.forEach(key => {
                  newExpanded[key] = true;
                });
              }
              setExpandedSections(newExpanded);
            }}
            style={{ width: '100%' }}
          >
            {proposal.items?.map((item, itemIndex) => (
              <Panel
                key={`product_${item.proposal_item_id}`}
                header={
                  <Space>
                    <Text strong>Product: {item.item_name}</Text>
                  </Space>
                }
              >
                <Space orientation="vertical" size="large" style={{ width: '100%' }}>
                  {/* Product Images */}
                  {pdfOptions[optionsKey].showProductImages && (
                    <div>
                      {templateType === "pretty_proposal" 
                        ? renderProductImageSelection(item.proposal_item_id, item.all_images)
                        : renderImageSelection('Product', item.proposal_item_id, item.all_images, 'Product Images')
                      }
                      
                      {/* Image Upload */}
                      <div>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Upload Additional Product Images</Text>
                        <Upload {...uploadProps(item.proposal_item_id)}>
                          <Button
                            icon={<UploadOutlined />}
                            disabled={uploadingImages[item.proposal_item_id]}
                            loading={uploadingImages[item.proposal_item_id]}
                          >
                            {uploadingImages[item.proposal_item_id] ? 'Uploading...' : 'Upload Images'}
                          </Button>
                        </Upload>
                      </div>
                    </div>
                  )}

                  {/* Product Information Fields */}
                  {pdfOptions[optionsKey].showProductInfo && (
                    renderFieldSelection('ProductInfo', DEFAULT_PRODUCT_INFO_FIELDS, 'Product Information Fields')
                  )}

                  {/* Pieces Section */}
                  {pdfOptions[optionsKey].showPieces && item.pieces && item.pieces.length > 0 && (
                    <div>
                      <Title level={5}>Pieces</Title>
                      <Space orientation="vertical" size="large" style={{ width: '100%' }}>
                        {item.pieces.map((piece, pieceIndex) => (
                          <Card
                            key={piece.piece_id || pieceIndex}
                            size="small"
                            style={{ borderLeft: '3px solid #1890ff', paddingLeft: 16 }}
                            title={
                              <Text type="secondary">
                                {piece.internal_manufacturer_code || piece.name || `Piece ${pieceIndex + 1}`}
                              </Text>
                            }
                          >
                            <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
                              {/* Piece Images */}
                              {pdfOptions[optionsKey].showPieceImages && (
                                <div>
                                  {renderImageSelection('Piece', piece.piece_id || pieceIndex, piece.proposal_images || piece.images, 'Piece Images')}
                                </div>
                              )}

                              {/* Piece Information Fields */}
                              {pdfOptions[optionsKey].showPieceInfo && (
                                renderFieldSelection('PieceInfo', DEFAULT_PIECE_INFO_FIELDS, 'Piece Information Fields')
                              )}

                              {/* Materials Section */}
                              {pdfOptions[optionsKey].showMaterials && piece.materials && piece.materials.length > 0 && (
                                <div>
                                  <Title level={5}>Materials</Title>
                                  <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
                                    {piece.materials.map((material, materialIndex) => (
                                      <Card
                                        key={material.material_id || materialIndex}
                                        size="small"
                                        style={{ borderLeft: '2px solid #d9d9d9', paddingLeft: 12 }}
                                        title={
                                          <Text type="secondary" style={{ fontSize: 14 }}>
                                            {material.name}
                                          </Text>
                                        }
                                      >
                                        <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                                          {/* Material Images */}
                                          {pdfOptions[optionsKey].showMaterialImages && material.material_image_path && (
                                            <div>
                                              <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>Material Image:</Text>
                                              <Row gutter={[16, 16]}>
                                                <Col xs={12} sm={8} md={6}>
                                                  <Card size="small">
                                                    <Image
                                                      src={getImageUrl(material.material_image_path)}
                                                      alt={material.name}
                                                      style={{ width: '100%', height: '100px', objectFit: 'cover' }}
                                                      preview={false}
                                                    />
                                                    <div style={{ marginTop: 8, textAlign: 'center' }}>
                                                      <Checkbox
                                                        checked={pdfOptions[optionsKey].selectedMaterialImages?.[material.material_id || materialIndex]?.includes('main') || false}
                                                        onChange={(e) => handleImageSelection('Material', material.material_id || materialIndex, 'main', e.target.checked)}
                                                      >
                                                        <Text style={{ fontSize: 12 }}>Include in PDF</Text>
                                                      </Checkbox>
                                                    </div>
                                                  </Card>
                                                </Col>
                                              </Row>
                                            </div>
                                          )}

                                          {/* Material Information Fields */}
                                          {pdfOptions[optionsKey].showMaterialInfo && (
                                            renderFieldSelection('MaterialInfo', DEFAULT_MATERIAL_INFO_FIELDS, 'Material Information Fields')
                                          )}
                                        </Space>
                                      </Card>
                                    ))}
                                  </Space>
                                </div>
                              )}
                            </Space>
                          </Card>
                        ))}
                      </Space>
                    </div>
                  )}
                </Space>
              </Panel>
            ))}
          </Collapse>
        </Card>

      {/* Image Description Editing Modal */}
      <Modal
        title="Edit Image Description"
        open={!!editingImageId}
        onCancel={() => {
          setEditingImageId(null);
          setEditingDescription('');
        }}
        onOk={() => {
          handleImageDescriptionUpdate(editingImageId, editingDescription);
          setEditingImageId(null);
          setEditingDescription('');
        }}
        okText="Save"
        cancelText="Cancel"
      >
        <Input
          value={editingDescription}
          onChange={(e) => setEditingDescription(e.target.value)}
          placeholder="Image description"
        />
      </Modal>

      {/* Generate PDF Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        <Button type="primary" size="large" onClick={handleDownloadPDF}>
          Generate Clean Proposal PDF
        </Button>
      </div>
    </Space>
  );
};

export default CleanProposalPDFOptions; 